import React, { useState, useCallback, KeyboardEvent } from 'react';
import { Button } from '@dynamon-as/ui';
import { Send } from '@mui/icons-material';
import styles from './ChatComposer.module.css';

/**
 * Props for ChatComposer component
 */
export interface ChatComposerProps {
    /**
     * Callback when sending a message.
     * Return `false` to keep the text in the composer (send failed);
     * any other result (true/void) clears the textarea.
     */
    onSend: (text: string) => Promise<boolean | void> | boolean | void;
    /** Whether a send is currently in progress */
    isSending?: boolean;
    /** Whether the composer is disabled */
    disabled?: boolean;
    /** Placeholder text for the textarea */
    placeholder?: string;
}

/**
 * Renders a chat composer with textarea, send button, and character counter
 */
export const ChatComposer = React.memo(function ChatComposer({
    onSend,
    isSending = false,
    disabled = false,
    placeholder = 'Skriv en kommentar...',
}: ChatComposerProps): React.ReactElement {
    const [text, setText] = useState('');

    const handleSend = useCallback(async (): Promise<void> => {
        const trimmed = text.trim();

        if (!trimmed || isSending || disabled) {
            return;
        }

        try {
            const result = await onSend(trimmed);
            if (result !== false) {
                setText('');
            }
        } catch {
            // Keep text if sending fails
        }
    }, [text, isSending, disabled, onSend]);

    const handleKeyDown = useCallback(
        (e: KeyboardEvent<HTMLTextAreaElement>): void => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void handleSend();
            }
        },
        [handleSend],
    );

    const isEmpty = text.trim().length === 0;

    return (
        <div
            className={styles.container}
            data-testid="chat-composer"
        >
            <textarea
                className={styles.textarea}
                rows={2}
                maxLength={1000}
                value={text}
                onChange={(e): void => setText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                disabled={disabled || isSending}
                data-testid="chat-composer-textarea"
                aria-label="Skriv en kommentar"
            />
            <div className={styles.footer}>
                <span className={styles.counter}>{text.length}/1000</span>
                <Button
                    text={isSending ? 'Sender...' : 'Send'}
                    color="primary"
                    variant="flat"
                    size="small"
                    Icon={(): React.ReactElement => <Send fontSize="small" />}
                    onClick={handleSend}
                    disabled={isEmpty || isSending || disabled}
                    data-testid="chat-composer-send"
                />
            </div>
        </div>
    );
});
